import { Toast, ToastMessage } from 'primereact/toast';
import React, { RefObject } from 'react';

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'center';

const DEFAULT_POSITION: ToastPosition = 'top-right';

/**
 * Toast service for displaying notifications
 * Uses a singleton pattern to allow global toast access
 */
class ToastUtility {
  private toastRefs: Map<ToastPosition, RefObject<Toast | null>> = new Map();

  /**
   * Register a toast component reference for a specific position
   * Called by ToastContainer on mount
   */
  setToastRef(position: ToastPosition, ref: RefObject<Toast | null>) {
    this.toastRefs.set(position, ref);
  }

  /**
   * Clear a toast reference on unmount
   */
  clearToastRef(position: ToastPosition) {
    this.toastRefs.delete(position);
  }

  /**
   * Show a custom toast message
   */
  show(message: ToastMessage, position: ToastPosition = DEFAULT_POSITION) {
    const ref = this.toastRefs.get(position);
    ref?.current?.show(message);
  }

  /**
   * Show a success toast
   */
  showSuccess(summary: string, detail?: string, position?: ToastPosition) {
    this.show(
      {
        severity: 'success',
        summary,
        detail,
        life: 3000,
      },
      position
    );
  }

  /**
   * Show an error toast
   */
  showError(
    summary: string,
    detail?: string | React.ReactNode,
    position?: ToastPosition
  ) {
    this.show(
      {
        severity: 'error',
        summary,
        detail,
        life: 5000,
      },
      position
    );
  }

  /**
   * Show an info toast
   */
  showInfo(summary: string, detail?: string, position?: ToastPosition) {
    this.show(
      {
        severity: 'info',
        summary,
        detail,
        life: 3000,
      },
      position
    );
  }

  /**
   * Show a warning toast
   */
  showWarn(summary: string, detail?: string, position?: ToastPosition) {
    this.show(
      {
        severity: 'warn',
        summary,
        detail,
        life: 4000,
      },
      position
    );
  }
}

// Export singleton instance
export const toastUtility = new ToastUtility();
